import React from "react";
import styled from "styled-components";
import { Text } from "@swiggy-private/dls-web-core/dist/components/Text";
import { GenericIcon } from "@swiggy-private/dls-web-icons/dist/GenericIconWeb";
import { Svg12 as ChevronDown } from "@swiggy-private/dls-web-icons/dist/web/generic/chevronDownFilled";

interface SelectOption {
    value: string | number;
    label: string;
}

interface StyledSelectProps {
    value: string;
    onChange: (value: string) => void;
    options: SelectOption[];
    placeholder?: string;
    label?: string;
    disabled?: boolean;
    error?: boolean;
    errorMessage?: string;
    floatingLabel?: boolean;
}

export const StyledSelect: React.FC<StyledSelectProps> = ({
    value,
    onChange,
    options,
    placeholder = "Select an option",
    label,
    disabled = false,
    error = false,
    errorMessage,
    floatingLabel = false,
}) => {
    const hasValue = value && value !== "";
    const isFloating = floatingLabel && hasValue;

    return (
        <SelectContainer>
            <SelectWrapper>
                <NativeSelect
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    disabled={disabled}
                    error={error}
                    floatingLabel={floatingLabel}
                >
                    <option value="" disabled>
                        {placeholder}
                    </option>
                    {options.map((option) => (
                        <option key={option.value} value={option.value}>
                            {option.label}
                        </option>
                    ))}
                </NativeSelect>

                <ChevronIcon>
                    <GenericIcon Icon={ChevronDown} fillColor="text_Highest_Emphasis" />
                </ChevronIcon>
            </SelectWrapper>

            {error && errorMessage && (
                <Text variant="V2.body2.neutral.regular" color="negative">
                    {errorMessage}
                </Text>
            )}
        </SelectContainer>
    );
};

const SelectContainer = styled.div`
    position: relative;
    width: 100%;
`;

const SelectWrapper = styled.div`
    position: relative;
    width: 100%;
`;

const NativeSelect = styled.select<{ error: boolean; floatingLabel: boolean }>`
    width: 100%;
    height: 56px;
    border-radius: ${({ theme }) => theme.spacing.medium};
    border-width: 1px;
    border-color: ${({ theme, error }) =>
        error ? theme.colors[theme.colorMode].negative : theme.colors[theme.colorMode].border_Secondary};
    background-color: ${({ theme }) => theme.colors[theme.colorMode].background_Primary};
    padding-left: ${({ theme }) => theme.spacing.medium};
    padding-right: ${({ theme }) => theme.spacing.xxLarge}; /* Space for chevron */
    appearance: none;
    -webkit-appearance: none;
    -moz-appearance: none;
    cursor: pointer;
    outline: none;
    transition: all 0.2s ease;
    font-size: 16px;
    font-weight: 500;
    color: ${({ theme }) => theme.colors[theme.colorMode].text_High_Emphasis};
    display: flex;
    align-items: center;

    &:focus {
        border-color: ${({ theme, error }) =>
            error ? theme.colors[theme.colorMode].negative : theme.colors[theme.colorMode].primary};
        outline: none;
    }

    &:disabled {
        background-color: ${({ theme }) => theme.colors[theme.colorMode].background_Secondary};
        cursor: not-allowed;
        color: ${({ theme }) => theme.colors[theme.colorMode].text_disabled};
    }

    option {
        padding: ${({ theme }) => theme.spacing.small};
        background-color: ${({ theme }) => theme.colors[theme.colorMode].background_Primary};
        color: ${({ theme }) => theme.colors[theme.colorMode].text_High_Emphasis};
        font-weight: 500;
    }

    option:disabled {
        color: ${({ theme }) => theme.colors[theme.colorMode].text_Med_Emphasis};
        font-weight: 400;
    }
`;

const ChevronIcon = styled.div`
    position: absolute;
    right: ${({ theme }) => theme.spacing.medium};
    top: 50%;
    transform: translateY(-50%);
    pointer-events: none;
    display: flex;
    align-items: center;
    justify-content: center;
`;

const LabelText = styled.label<{ error: boolean }>`
    display: block;
    margin-bottom: 8px;
    font-size: 12px;
    font-weight: 700;
    color: ${({ theme, error }) => 
        error 
            ? theme.colors[theme.colorMode].negative 
            : theme.colors[theme.colorMode].text_Low_Emphasis
    };
    line-height: 1;
`;
